import { useState, useRef, useEffect } from "react";
import { X, Loader2, CheckCircle2, PlayCircle, BadgePercent } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import axios from "axios";
import { useDemoModal } from "@/context/DemoModalContext";
import { EMAIL_REGEX } from "@/lib/validators";
import { resolveApiError } from "@/lib/api-errors";

const API_URL = process.env.REACT_APP_API_URL || "";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const FIELD =
  "mt-1.5 w-full rounded-xl border border-violet-light bg-white px-4 py-3 text-[15px] text-ink shadow-sm outline-none transition-all duration-150 focus:border-violet-fuerte focus:ring-2 focus:ring-violet-fuerte/20 placeholder:text-ink-3";
const LABEL = "block text-sm font-semibold text-ink";
const ERROR = "mt-1 text-[13px] text-red-500";

export default function DemoRequestModal() {
  const { open, closeModal } = useDemoModal();
  const [form, setForm] = useState({ name: "", churchName: "", email: "", city: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const overlayRef = useRef(null);

  // Cierre con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeModal]);

  // Lock scroll del body mientras el modal está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setForm({ name: "", churchName: "", email: "", city: "", phone: "" });
      setErrors({});
      setTurnstileToken("");
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  if (!open) return null;

  function validate() {
    const e = {};
    if (!form.name.trim()) e.name = "El nombre es obligatorio.";
    if (!form.churchName.trim()) e.churchName = "El nombre de la iglesia es obligatorio.";
    if (!form.email.trim()) e.email = "El correo es obligatorio.";
    else if (!EMAIL_REGEX.test(form.email)) e.email = "Correo no válido.";
    if (!form.city.trim()) e.city = "La ciudad es obligatoria.";
    if (!form.phone.trim()) e.phone = "El teléfono es obligatorio.";
    if (TURNSTILE_SITE_KEY && !turnstileToken) e.turnstile = "Completa la verificación.";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setStatus("loading");
    try {
      await axios.post(`${API_URL}/public/trial/start`, {
        name: form.name.trim(),
        churchName: form.churchName.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        phone: form.phone.trim(),
        turnstileToken: turnstileToken || undefined,
      });
      setStatus("success");
    } catch (err) {
      if (err?.response?.data?.code === "TURNSTILE_FAILED") {
        setTurnstileToken("");
      }
      setErrorMsg(resolveApiError(err, {
        RATE_LIMITED: "Demasiadas solicitudes. Inténtalo más tarde.",
        TURNSTILE_FAILED: "La verificación falló. Inténtalo de nuevo.",
        EMAIL_ALREADY_HAS_ACTIVE_TRIAL: "Ya tienes una prueba activa. Revisa tu correo para acceder.",
        EMAIL_ALREADY_ACTIVE: "Este correo ya tiene una cuenta activa. Inicia sesión directamente.",
        BLOCKED_EMAIL_DOMAIN: "Ese dominio de correo no está permitido. Usa un correo corporativo o personal (no uno temporal o desechable).",
      }));
      setStatus("error");
    }
  }

  // Oferta fundador forzada activa hasta nuevo aviso.
  const founderActive = true;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) closeModal(); }}
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ background: "rgba(26,17,53,0.45)", backdropFilter: "blur(4px)" }}
      role="dialog"
      aria-modal="true"
      aria-label="Prueba gratis 14 días"
    >
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-[0_32px_80px_-16px_rgba(26,17,53,0.3)] overflow-hidden">
        {/* Top bar */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b border-violet-light"
          style={{ background: "linear-gradient(135deg, #EEEBFF 0%, #F5F3FF 100%)" }}
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-fuerte shadow-[0_4px_14px_-4px_rgba(74,58,138,0.4)]">
              <PlayCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display text-[15px] font-bold text-ink leading-tight">Prueba gratis 14 días</p>
              <p className="text-[12px] text-ink-2">Sin esperas ni llamadas. Acceso en minutos, lo configuras tú.</p>
            </div>
          </div>
          <button
            onClick={closeModal}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-violet-light bg-white text-ink-3 transition-colors hover:text-ink"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EDF8F3]">
                <CheckCircle2 className="h-8 w-8 text-[#2D8A5E]" />
              </div>
              <div>
                <p className="font-display text-lg font-bold text-ink">¡Tu prueba está lista!</p>
                <p className="mt-2 text-[15px] text-ink-2 leading-relaxed">
                  Revisa tu correo electrónico. Te hemos enviado un enlace para <strong>crear tu contraseña</strong> y acceder a la prueba de 14 días.
                </p>
              </div>
              <button
                onClick={closeModal}
                className="mt-2 inline-flex h-11 items-center justify-center rounded-xl bg-violet-fuerte px-8 text-[15px] font-semibold text-white transition-colors hover:bg-violet-oscuro"
              >
                Cerrar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {founderActive && (
                <div className="flex items-center gap-2.5 rounded-xl border border-gold/30 bg-[#FFFBEA] px-4 py-2.5">
                  <BadgePercent className="h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                  <p className="text-[13px] font-semibold text-gold-text">
                    Oferta Fundadores activa · Precio bloqueado de por vida
                  </p>
                </div>
              )}
              <div>
                <label htmlFor="modal-name" className={LABEL}>Nombre</label>
                <input
                  id="modal-name"
                  type="text"
                  placeholder="Tu nombre"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className={FIELD}
                  autoComplete="name"
                />
                {errors.name && <p className={ERROR}>{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="modal-church-name" className={LABEL}>Nombre de la iglesia</label>
                <input
                  id="modal-church-name"
                  type="text"
                  placeholder="Ej: Iglesia Gracia y Verdad"
                  value={form.churchName}
                  onChange={(e) => setForm((p) => ({ ...p, churchName: e.target.value }))}
                  className={FIELD}
                />
                {errors.churchName && <p className={ERROR}>{errors.churchName}</p>}
              </div>

              <div>
                <label htmlFor="modal-email" className={LABEL}>Correo electrónico</label>
                <input
                  id="modal-email"
                  type="email"
                  placeholder="pastor@miIglesia.com"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className={FIELD}
                  autoComplete="email"
                />
                {errors.email && <p className={ERROR}>{errors.email}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="modal-city" className={LABEL}>Ciudad</label>
                  <input
                    id="modal-city"
                    type="text"
                    placeholder="Tu ciudad"
                    value={form.city}
                    onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                    className={FIELD}
                  />
                  {errors.city && <p className={ERROR}>{errors.city}</p>}
                </div>
                <div>
                  <label htmlFor="modal-phone" className={LABEL}>Teléfono</label>
                  <input
                    id="modal-phone"
                    type="tel"
                    placeholder="+34 612 345 678"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    className={FIELD}
                    autoComplete="tel"
                  />
                  {errors.phone && <p className={ERROR}>{errors.phone}</p>}
                </div>
              </div>

              {TURNSTILE_SITE_KEY && (
                <div>
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    options={{ theme: "light", language: "es" }}
                  />
                  {errors.turnstile && <p className={ERROR}>{errors.turnstile}</p>}
                </div>
              )}

              {status === "error" && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-[13px] text-red-600">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[17px] font-semibold text-white shadow-[0_6px_24px_-6px_rgba(74,58,138,0.45)] transition-all duration-300 hover:bg-violet-oscuro disabled:opacity-60"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Creando tu prueba...</>
                ) : (
                  "Empezar prueba gratis"
                )}
              </button>

              <p className="text-center text-[12px] text-ink-3">
                Acceso inmediato · Sin tarjeta · Sin compromiso
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
